import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Phone, Sparkles } from "lucide-react";
import { useSite } from "@/context/SiteContext";

export default function Hero() {
  const { content, config } = useSite();
  const wa = `https://wa.me/${config?.whatsapp}?text=${encodeURIComponent("Olá DeskcodeBR, gostaria de uma proposta.")}`;

  return (
    <section data-testid="hero-section" className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden bg-gradient-to-br from-[#1A1858] via-[#2E2A8C] to-[#0a0a2e]">
      <div className="absolute inset-0 tech-grid opacity-40"></div>
      <div className="glow-orb w-[500px] h-[500px] -top-32 -right-32 bg-dc-accent/30"></div>
      <div className="glow-orb w-[400px] h-[400px] -bottom-32 -left-20 bg-dc-primary/40"></div>

      {/* Decorative animated lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-25" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="lg" x1="0" x2="1">
            <stop offset="0" stopColor="#EE5524" stopOpacity="0" />
            <stop offset="0.5" stopColor="#EE5524" stopOpacity="1" />
            <stop offset="1" stopColor="#EE5524" stopOpacity="0" />
          </linearGradient>
        </defs>
        <line x1="0" y1="20%" x2="100%" y2="20%" stroke="url(#lg)" strokeWidth="1" className="animated-line" />
        <line x1="0" y1="60%" x2="100%" y2="60%" stroke="url(#lg)" strokeWidth="1" className="animated-line" style={{ animationDelay: "1s" }} />
        <line x1="0" y1="85%" x2="100%" y2="85%" stroke="url(#lg)" strokeWidth="1" className="animated-line" style={{ animationDelay: "2s" }} />
      </svg>

      <div className="dc-container relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-xs font-medium px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-3.5 h-3.5 text-dc-accent" /> Tecnologia premium para empresas
            </div>
            <h1 data-testid="hero-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-[1.05] tracking-tight mb-6">
              {content.hero_title || "Tecnologia, Infraestrutura e Desenvolvimento para Empresas"}
            </h1>
            <p data-testid="hero-subtitle" className="text-base md:text-lg text-blue-50/90 leading-relaxed max-w-2xl mb-10">
              {content.hero_subtitle}
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/orcamento"
                data-testid="hero-cta-orcamento"
                className="inline-flex items-center gap-2 bg-white text-dc-secondary hover:bg-dc-accent hover:text-dc-secondary px-7 py-4 rounded-full font-semibold transition-all shadow-2xl hover:-translate-y-0.5"
              >
                Solicitar orçamento <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/visita-tecnica"
                data-testid="hero-cta-visita"
                className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-dc-accent text-white hover:text-dc-accent px-7 py-4 rounded-full font-semibold transition-all"
              >
                <Phone className="w-4 h-4" /> Visita técnica
              </Link>
              <a
                href={wa}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-cta-whatsapp"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb557] text-white px-7 py-4 rounded-full font-semibold transition-all shadow-xl"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-5 relative hidden lg:block"
          >
            <div className="relative float-anim">
              <div className="absolute -inset-4 bg-gradient-to-tr from-dc-accent/20 to-dc-primary/20 rounded-3xl blur-2xl"></div>
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-3xl p-6 shadow-2xl">
                <div className="bg-[#0a1128] rounded-2xl p-5 font-mono text-xs">
                  <div className="flex gap-1.5 mb-4">
                    <span className="w-3 h-3 rounded-full bg-red-400"></span>
                    <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
                    <span className="w-3 h-3 rounded-full bg-green-400"></span>
                    <span className="ml-3 text-slate-500 text-[10px]">deskcodebr@infra:~</span>
                  </div>
                  <div className="space-y-2 text-slate-300">
                    <div><span className="text-dc-accent">$</span> uptime</div>
                    <div className="text-slate-500 pl-4">99.99% • 247 dias</div>
                    <div><span className="text-dc-accent">$</span> systemctl status firewall</div>
                    <div className="text-green-400 pl-4">● active (running)</div>
                    <div><span className="text-dc-accent">$</span> backup --status</div>
                    <div className="text-green-400 pl-4">✓ Sincronizado | 3-2-1</div>
                    <div><span className="text-dc-accent">$</span> deploy production</div>
                    <div className="text-blue-300 pl-4">→ build • test • ship 🚀</div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2">
                  <Card label="CPU" value="32%" color="text-emerald-400" />
                  <Card label="RAM" value="58%" color="text-amber-400" />
                  <Card label="NET" value="1.2G" color="text-dc-accent" />
                </div>
              </div>

              <div className="absolute -bottom-6 -right-6 bg-white text-dc-secondary px-4 py-3 rounded-xl shadow-xl border border-white/40 flex items-center gap-2 text-sm font-semibold">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span> SLA 24/7 ativo
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

const Stat = ({ label, value }) => null;

const Card = ({ label, value, color }) => (
  <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
    <div className={`font-heading font-bold ${color} text-lg`}>{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
  </div>
);
